import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, Select, InputNumber, Button, message, Row, Col,
  Typography, Space, Spin, Upload, Avatar, Divider,
} from 'antd';
import {
  SaveOutlined, ArrowLeftOutlined, UploadOutlined, LoadingOutlined, EditOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';
import CepInput from '../../components/CepInput';

const { Title, Text } = Typography;

const SKILL_LEVELS = [
  { value: 'iniciante', label: 'Iniciante' },
  { value: 'recreativo', label: 'Recreativo' },
  { value: 'intermediario', label: 'Intermediário' },
  { value: 'avancado', label: 'Avançado' },
  { value: 'semi_pro', label: 'Semi-profissional' },
];
const DAYS = [
  { value: 0, label: 'Dom' }, { value: 1, label: 'Seg' }, { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' }, { value: 4, label: 'Qui' }, { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
];
const HORARIOS = ['18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '08:00', '09:00', '10:00'];

export default function TeamOwnerEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/community/team-owner/${id}/dashboard`);
        const t = r.data.team;
        form.setFieldsValue({
          name: t.name, description: t.description, skill_level: t.skill_level || 'recreativo',
          cep: t.cep, address: t.address, city: t.city, state: t.state, neighborhood: t.neighborhood,
          court_name: t.court_name, latitude: t.latitude, longitude: t.longitude,
          days_of_week: t.days_of_week || [], game_times: t.game_times || [],
          guest_price: t.guest_price, monthly_price: t.monthly_price,
          search_radius_km: t.search_radius_km || 15,
          conduct_code: t.conduct_code, is_public: t.is_public !== false,
          logo_url: t.logo_url,
        });
        if (t.logo_url) setLogoUrl(t.logo_url);
      } catch {
        message.error('Erro ao carregar time');
      } finally { setLoading(false); }
    })();
  }, [id, form]);

  const save = async () => {
    try {
      const vals = await form.validateFields();
      setSaving(true);
      await api.put(`/community/team-owner/${id}`, vals);
      message.success('Time atualizado');
      nav(`/time/dashboard/${id}`);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 16 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <Title level={4} style={{ margin: 0 }}><EditOutlined /> Editar time</Title>
            <Text type="secondary">Ajusta as info do teu time-comunidade.</Text>
          </div>
          <Button icon={<ArrowLeftOutlined />} onClick={() => nav(`/time/dashboard/${id}`)}>Voltar</Button>
        </div>

        <Form form={form} layout="vertical">
          <Divider orientation="left" plain>Identidade</Divider>
          <Form.Item label="Nome do time" name="name" rules={[{ required: true, min: 3 }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Descrição" name="description">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
          <Form.Item label="Logo do time" extra="PNG/JPG/WebP, máx 2MB.">
            <Space>
              {logoUrl && <Avatar size={64} src={absoluteUploadUrl(logoUrl)} />}
              <Upload
                accept="image/png,image/jpeg,image/webp"
                showUploadList={false}
                customRequest={async ({ file, onSuccess, onError }) => {
                  setUploading(true);
                  try {
                    const fd = new FormData();
                    fd.append('image', file);
                    const r = await api.post('/community/uploads/image', fd, {
                      headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    setLogoUrl(r.data.url);
                    form.setFieldsValue({ logo_url: r.data.url });
                    onSuccess(r.data);
                    message.success('Logo enviada');
                  } catch (err) {
                    onError(err);
                    message.error(err?.response?.data?.message || 'Erro ao enviar');
                  } finally { setUploading(false); }
                }}
              >
                <Button icon={uploading ? <LoadingOutlined /> : <UploadOutlined />} loading={uploading}>
                  {logoUrl ? 'Trocar' : 'Enviar logo'}
                </Button>
              </Upload>
            </Space>
          </Form.Item>
          <Form.Item name="logo_url" hidden><Input /></Form.Item>
          <Form.Item label="Nível" name="skill_level">
            <Select options={SKILL_LEVELS} />
          </Form.Item>

          <Divider orientation="left" plain>Quadra</Divider>
          <Form.Item label="Nome da quadra" name="court_name"><Input /></Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={10}>
              <Form.Item label="CEP" name="cep" rules={[{ required: true }]}>
                <CepInput onAddressChange={(g) => {
                  if (g) form.setFieldsValue({
                    address: g.address || undefined, city: g.city || undefined,
                    state: g.state || undefined, neighborhood: g.neighborhood || undefined,
                    latitude: g.latitude || undefined, longitude: g.longitude || undefined,
                  });
                }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={14}>
              <Form.Item label="Endereço" name="address" rules={[{ required: true }]}><Input /></Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={12} md={8}><Form.Item label="Cidade" name="city" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={12} md={4}><Form.Item label="UF" name="state" rules={[{ required: true }]}><Input maxLength={2} /></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item label="Bairro" name="neighborhood"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="latitude" hidden><Input /></Form.Item>
          <Form.Item name="longitude" hidden><Input /></Form.Item>

          <Divider orientation="left" plain>Jogos & valores</Divider>
          <Form.Item label="Dias" name="days_of_week"><Select mode="multiple" options={DAYS} /></Form.Item>
          <Form.Item label="Horários" name="game_times">
            <Select mode="tags" options={HORARIOS.map(h => ({ value: h, label: h }))} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={12}><Form.Item label="Avulso (R$)" name="guest_price"><InputNumber min={0} step={5} style={{ width: '100%' }} /></Form.Item></Col>
            <Col xs={12}><Form.Item label="Mensalidade (R$)" name="monthly_price"><InputNumber min={0} step={10} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item label="Raio de busca (km)" name="search_radius_km">
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Código de conduta" name="conduct_code">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>

          <div style={{ textAlign: 'right', marginTop: 20 }}>
            <Button type="primary" size="large" icon={<SaveOutlined />} loading={saving} onClick={save}>
              Salvar alterações
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
}
